import React, { useEffect, useMemo, useState } from "react";
import { Calendar } from "lucide-react";
import DownArrow from "../../../../assets/icons/DownArrow";

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const normalizeDate = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const parseDateValue = (value) => {
  if (!value) return normalizeDate(new Date());
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return normalizeDate(new Date());
  return normalizeDate(new Date(year, month - 1, day));
};

export const formatDateInputValue = (date = new Date()) => {
  const normalized = normalizeDate(date);
  const year = normalized.getFullYear();
  const month = String(normalized.getMonth() + 1).padStart(2, "0");
  const day = String(normalized.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (date) =>
  date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

function FinancialSingleDatePicker({ value, onChange, maxDate }) {
  const selectedDate = useMemo(() => parseDateValue(value), [value]);
  const maxDateObj = useMemo(
    () => (maxDate ? parseDateValue(maxDate) : normalizeDate(new Date())),
    [maxDate]
  );
  const [showPicker, setShowPicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(selectedDate);

  useEffect(() => {
    if (showPicker) {
      setCurrentMonth(selectedDate);
    }
  }, [showPicker, selectedDate]);

  const days = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const values = [];
    for (let i = 0; i < startingDayOfWeek; i += 1) {
      values.push(null);
    }
    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      values.push(new Date(year, month, day));
    }
    return values;
  }, [currentMonth]);

  const isDisabled = (date) => date > maxDateObj;
  const isSelected = (date) => date.getTime() === selectedDate.getTime();

  const handleOpenPicker = () => setShowPicker(true);

  const handleDayClick = (date) => {
    if (isDisabled(date)) return;
    onChange(formatDateInputValue(date));
    setShowPicker(false);
  };

  const navigateMonth = (direction) => {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(nextMonth);
  };

  return (
    <>
      <div
        className="add-journal-date-field"
        onClick={handleOpenPicker}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleOpenPicker();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Select transaction date"
      >
        <Calendar size={16} className="add-journal-date-field-icon" />
        <span className="add-journal-date-field-text">
          {formatDisplayDate(selectedDate)}
        </span>
      </div>

      {showPicker && (
        <div
          className="financial-date-picker-backdrop add-journal-date-picker-backdrop"
          onClick={() => setShowPicker(false)}
        >
          <div
            className="financial-date-picker financial-date-picker-single"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="financial-date-picker-header">
              <button
                type="button"
                className="financial-date-picker-nav-btn"
                onClick={() => navigateMonth(-1)}
              >
                <span className="financial-date-picker-nav-icon rotate-left">
                  <DownArrow />
                </span>
              </button>
              <h3 className="financial-date-picker-month-year">
                {currentMonth.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </h3>
              <button
                type="button"
                className="financial-date-picker-nav-btn"
                onClick={() => navigateMonth(1)}
              >
                <span className="financial-date-picker-nav-icon rotate-right">
                  <DownArrow />
                </span>
              </button>
            </div>

            <div className="financial-date-picker-weekdays">
              {WEEKDAYS.map((weekday) => (
                <div key={weekday} className="financial-date-picker-weekday">
                  {weekday}
                </div>
              ))}
            </div>

            <div className="financial-date-picker-days">
              {days.map((day, index) => {
                if (!day) {
                  return (
                    <div
                      key={`empty-${index}`}
                      className="financial-date-picker-day empty"
                    />
                  );
                }

                const disabled = isDisabled(day);
                const selected = isSelected(day);
                const className = [
                  "financial-date-picker-day",
                  disabled ? "disabled" : "",
                  selected ? "selected-single" : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    className={className}
                    onClick={() => handleDayClick(normalizeDate(day))}
                    disabled={disabled}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default FinancialSingleDatePicker;
